import type React from 'react'
import { useEffect, useRef, useState } from 'react'

import { getMouseY } from '../utils/mouse'

import styles from './SheetContent.module.css'

const BEGIN_DRAG_OFFSET = 0

export function SheetContent({
	children,
	onTop,
	parentHeight,
	footerMargin,
}: {
	readonly children: React.ReactNode
	readonly onTop: (e: number) => void
	readonly parentHeight: number
	readonly footerMargin: number
}) {
	const containerRef = useRef<HTMLDivElement>(null)
	const initialRef = useRef<number | null>(null)
	const isDraggingRef = useRef<boolean | null>(false)
	const [offset, setOffset] = useState(0)

	function onDragging(e: TouchEvent) {
		isDraggingRef.current = true
		const delta = getMouseY(e) - (initialRef?.current ?? 0)
		const topOffset = (containerRef?.current?.clientHeight ?? 0) - parentHeight
		if (delta < 1 && delta > -topOffset) {
			setOffset(delta)
		} else {
			// User scrolled to top or bottom limit
			window.removeEventListener('touchmove', onDragging)
			window.removeEventListener('touchend', onDrop)
			// Sent event to parent to begin to drag and bounce animation control
			onTop(getMouseY(e))
		}
	}

	function onDrop() {
		isDraggingRef.current = false
		window.removeEventListener('touchmove', onDragging)
		window.removeEventListener('touchend', onDrop)
	}

	function onDragStart(e: React.TouchEvent<HTMLDivElement>) {
		e.stopPropagation()
		const reference = containerRef.current
		if ((reference && parentHeight < reference.clientHeight) || offset < 1) {
			if (initialRef?.current !== undefined) {
				initialRef.current = getMouseY(e) - offset
			}
			window.addEventListener('touchmove', onDragging)
			window.addEventListener('touchend', onDrop)
		} else {
			onTop(getMouseY(e))
		}
	}

	useEffect(() => {
		if (!isDraggingRef.current) {
			// When parent component height changes and sheet content is not dragging
			// have to reset position of the sheet content
			setOffset(BEGIN_DRAG_OFFSET)
		}
	}, [parentHeight])

	return (
		<div className={styles.content}>
			<div
				ref={containerRef}
				data-testid='sheet.draggable.content'
				className={styles.main}
				onTouchStart={onDragStart}
				style={{
					transform: `translateY(${offset}px)`,
					marginBottom: `${footerMargin}px`,
				}}
			>
				{children}
			</div>
		</div>
	)
}
